import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import qrcode from "qrcode-generator";
import { api, errorText } from "../api";
import { LOGO, Message } from "./ui";

/** QR-Code als SVG mit runden Punkten; die drei Ecken bleiben eckig, damit jede Kamera ihn sicher erkennt. */
export function QrCode({ text, size = 220 }: { text: string; size?: number }) {
  const { n, cells } = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(text);
    qr.make();
    const count = qr.getModuleCount();
    const list: [number, number][] = [];
    for (let r = 0; r < count; r++) for (let c = 0; c < count; c++) if (qr.isDark(r, c)) list.push([r, c]);
    return { n: count, cells: list };
  }, [text]);
  const finder = (r: number, c: number) => (r < 7 && c < 7) || (r < 7 && c >= n - 7) || (r >= n - 7 && c < 7);
  return (
    <svg className="qr" viewBox={`-2 -2 ${n + 4} ${n + 4}`} width={size} height={size} role="img" aria-label="QR-Code zum Verknüpfen des Handys">
      <rect x="-2" y="-2" width={n + 4} height={n + 4} rx="3" fill="#fff" />
      {cells.map(([r, c]) =>
        finder(r, c) ? (
          <rect key={`${r}-${c}`} x={c} y={r} width="1.02" height="1.02" fill="currentColor" />
        ) : (
          <circle key={`${r}-${c}`} cx={c + 0.5} cy={r + 0.5} r="0.44" fill="currentColor" />
        ),
      )}
    </svg>
  );
}

/** Zeigt einen QR-Code, mit dem sich das Handy ohne Passwort anmeldet. Erneuert sich, wenn er abläuft. */
export function PhoneLinkPanel() {
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const [error, setError] = useState("");
  const [left, setLeft] = useState(0);

  const fresh = useCallback(() => {
    setError("");
    api
      .post<{ url: string; expiresAt: string }>("/api/devices/link")
      .then(setLink)
      .catch((err) => setError(errorText(err)));
  }, []);
  useEffect(fresh, [fresh]);

  useEffect(() => {
    if (!link) return;
    const tick = () => {
      const s = Math.max(0, Math.round((new Date(link.expiresAt).getTime() - Date.now()) / 1000));
      setLeft(s);
      if (s === 0) fresh();
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [link, fresh]);

  return (
    <div className="plink">
      <div className="plink-qr">{link ? <QrCode text={link.url} /> : <div className="qr-wait" />}</div>
      <ol className="plink-steps">
        <li>Kamera am Handy öffnen und auf den Code richten.</li>
        <li>Den Link antippen. Die App öffnet sich bereits angemeldet.</li>
        <li>Im Browser „Zum Home-Bildschirm“ wählen. Fertig.</li>
      </ol>
      {link && (
        <p className="muted-s">
          Gültig noch {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")} Minuten, nur einmal verwendbar.
        </p>
      )}
      <Message>{error}</Message>
    </div>
  );
}

/** Seite, die das Handy nach dem Scannen öffnet. Der Code steht hinter dem #, damit er in keinem Protokoll landet. */
export function ConnectPhone() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  useEffect(() => {
    const token = window.location.hash.slice(1);
    if (!token) {
      setError("In diesem Link fehlt der Code. Bitte scannen Sie den QR-Code noch einmal.");
      return;
    }
    api
      .post("/api/devices/redeem", { token })
      .then(() => {
        history.replaceState(null, "", "/app");
        navigate("/app", { replace: true });
      })
      .catch((err) => setError(errorText(err)));
  }, [navigate]);
  return (
    <div className="hv-app">
      <div className="boot connect">
        {LOGO}
        {error ? (
          <div className="connect-msg">
            <b>Das hat nicht geklappt</b>
            <p>{error}</p>
            <a className="app-btn" href="/anmelden?weiter=/app">
              Mit Passwort anmelden
            </a>
          </div>
        ) : (
          <p className="connect-msg">Handy wird verknüpft …</p>
        )}
      </div>
    </div>
  );
}
